import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { calculatePaperProgress } from '../data/config';
import { loadAllPapersForStudy, getResponseDocId } from '../data/papersHelpers';
import {
  loadAssignmentsForStudy,
  removeAssignment,
} from '../data/assignmentsHelpers';
import {
  listMembershipsForOrg,
  removeMembership,
} from '../auth/membershipHelpers';
import { useActiveStudy } from '../hooks/useActiveStudy';
import { useSession } from '../auth/sessionContext';
import { buildResponsesCsv, downloadCsv } from '../data/exportHelpers';
import {
  User,
  CheckCircle,
  Edit,
  Download,
  Lock,
  Loader,
  Settings,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

const USING_FIREBASE_EMULATORS = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';

function isLocalTestUser(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith('@test.local');
}

  const AdminDashboard = () => {
  const { studyId, loading: studyLoading } = useActiveStudy();
  const { activeOrg, activeStudy, user } = useSession();
  const orgId = activeOrg?.orgId || null;
  const [researchers, setResearchers] = useState([]);
  const [allResponses, setAllResponses] = useState(new Map());
  const [assignmentsByReviewer, setAssignmentsByReviewer] = useState({});
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [memberStatus, setMemberStatus] = useState({ type: 'idle', message: '' });
  const [memberActionKey, setMemberActionKey] = useState('');

  const loadData = useCallback(async () => {
    if (!studyId || !orgId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);

      const loadedPapers = await loadAllPapersForStudy(studyId);
      setPapers(loadedPapers);

      // Scope the dashboard to the active org. The permissive list rule
      // means an unfiltered memberships query can leak reviewers from
      // other orgs into this screen.
      const usersList = (await listMembershipsForOrg(orgId))
        .filter((membership) => membership.role === 'reviewer');

      // Ticket 10: assigned papers now come from the assignments
      // collection (configuration/main.users was deleted by the
      // backfill). Build a map keyed by reviewerId.
      const assignmentsList = await loadAssignmentsForStudy(studyId);
      const byReviewer = {};
      assignmentsList.forEach((a) => {
        if (!byReviewer[a.reviewerId]) byReviewer[a.reviewerId] = [];
        byReviewer[a.reviewerId].push(a);
      });
      setAssignmentsByReviewer(byReviewer);

      const responsesSnapshot = await getDocs(collection(db, 'responses'));
      const responsesMap = new Map();
      responsesSnapshot.forEach((docSnap) => {
        responsesMap.set(docSnap.id, docSnap.data());
      });

      setResearchers(usersList);
      setAllResponses(responsesMap);
    } catch (err) {
      console.error('[admin-dashboard] load failed:', err);
      setResearchers([]);
      setAllResponses(new Map());
      setAssignmentsByReviewer({});
      setPapers([]);
      setMemberStatus({
        type: 'error',
        message: `Dashboard load failed: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, studyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExportCsv = async () => {
    if (!activeOrg || !activeStudy) {
      setExportStatus('No active org/study.');
      return;
    }
    setExporting(true);
    setExportStatus('');
    try {
      const { csv, rowCount, responseCount } = await buildResponsesCsv(
        activeOrg.orgId,
        activeStudy.studyId,
      );
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const safeStudy = (activeStudy.studyName || activeStudy.studyId)
        .replace(/[^a-zA-Z0-9]/g, '_')
        .slice(0, 40);
      downloadCsv(`responses_${safeStudy}_${stamp}.csv`, csv);
      setExportStatus(`Exported ${rowCount} rows from ${responseCount} responses.`);
    } catch (err) {
      setExportStatus(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadJson = (responseData, researcherEmail, paperTitle) => {
    if (!responseData) return;
    const jsonString = JSON.stringify(responseData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeEmail = researcherEmail.split('@')[0];
    const safeTitle = paperTitle.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    link.href = url;
    link.download = `responses_${safeEmail}_${safeTitle}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const removeReviewerAssignments = useCallback(async (reviewerUid) => {
    const assignments = assignmentsByReviewer[reviewerUid] || [];
    await Promise.all(
      assignments.map((assignment) =>
        removeAssignment({
          studyId: assignment.studyId || studyId,
          reviewerId: reviewerUid,
          paperId: assignment.paperId,
        }),
      ),
    );
  }, [assignmentsByReviewer, studyId]);

  const handleRemoveResearcher = async (researcher) => {
    const reviewerUid = researcher.uid || researcher.id;
    if (reviewerUid === user?.uid) {
      setMemberStatus({ type: 'error', message: 'You cannot remove yourself.' });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${researcher.email || reviewerUid} from this organization?`)) return;
    setMemberActionKey(researcher.id);
    setMemberStatus({ type: 'loading', message: 'Removing member...' });
    try {
      await removeReviewerAssignments(reviewerUid);
      await removeMembership(researcher.id);
      await loadData();
      setMemberStatus({
        type: 'success',
        message: 'Member removed. Existing response docs are left in Firestore.',
      });
    } catch (err) {
      setMemberStatus({ type: 'error', message: `Remove failed: ${err.message}` });
    } finally {
      setMemberActionKey('');
    }
  };

  const localTestResearchers = researchers.filter(
    (researcher) => isLocalTestUser(researcher.email) && researcher.uid !== user?.uid,
  );

  const handleBulkRemoveLocalTestUsers = async () => {
    if (localTestResearchers.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${localTestResearchers.length} local test members from this org?`)) return;
    setMemberActionKey('bulk-delete');
    setMemberStatus({ type: 'loading', message: 'Removing local test members...' });
    try {
      const results = await Promise.allSettled(
        localTestResearchers.map(async (researcher) => {
          const reviewerUid = researcher.uid || researcher.id;
          await removeReviewerAssignments(reviewerUid);
          await removeMembership(researcher.id);
        }),
      );
      const removed = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results.length - removed;
      await loadData();
      setMemberStatus({
        type: failed > 0 ? 'error' : 'success',
        message:
          failed > 0
            ? `Removed ${removed} local test members; ${failed} failed. Existing response docs are left in Firestore.`
            : `Removed ${removed} local test members. Existing response docs are left in Firestore.`,
      });
    } catch (err) {
      setMemberStatus({ type: 'error', message: `Bulk remove failed: ${err.message}` });
    } finally {
      setMemberActionKey('');
    }
  };

  if (loading || studyLoading) {
    return (
      <div className="flex h-[calc(100vh-80px)] w-full items-center justify-center">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  // Ticket 10: fast uuid lookup for papers so we can resolve each
  // assignment's paperId to the full paper doc in O(1).
  const papersById = {};
  papers.forEach((p) => {
    papersById[p.id] = p;
  });

  const memberStatusStyles = {
    idle: '',
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    loading: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  };

  return (
    <main className="p-4 lg:p-8">
      <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              Researcher Progress Overview
            </h2>
            {exportStatus && (
              <p className="text-xs text-slate-500 mt-1">{exportStatus}</p>
            )}
          </div>
          <div className="mt-4 sm:mt-0 flex gap-2">
            {USING_FIREBASE_EMULATORS && localTestResearchers.length > 0 && (
              <button
                onClick={handleBulkRemoveLocalTestUsers}
                disabled={memberActionKey === 'bulk-delete'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {memberActionKey === 'bulk-delete' ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
                Delete {localTestResearchers.length} local test users
              </button>
            )}
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              Export CSV
            </button>
            <Link
              to="/admin/assignments"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold"
            >
              <Settings size={16} />
              Assignments
            </Link>
            <Link
              to="/admin/members"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold"
            >
              <Settings size={16} />
              Members
            </Link>
            <Link
              to="/admin/questionnaire"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-semibold"
            >
              <Settings size={16} />
              Questionnaire
            </Link>
            <Link
              to="/admin/config"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
            >
              <Settings size={16} />
              Manage Configuration
            </Link>
          </div>
        </div>

        {memberStatus.message && (
          <div
            className={`mb-4 rounded-lg border px-4 py-3 text-sm flex items-start gap-3 ${memberStatusStyles[memberStatus.type]}`}
          >
            {memberStatus.type === 'loading' ? (
              <Loader size={16} className="animate-spin mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            )}
            <span>{memberStatus.message}</span>
          </div>
        )}

        <div className="space-y-6">
          {researchers.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
              No researchers found for this organization.
            </div>
          )}
          {researchers.map((researcher) => {
            const reviewerUid = researcher.uid || researcher.id;
            const assignmentsForThisReviewer =
              assignmentsByReviewer[reviewerUid] || [];
            const assignedPaperIds = assignmentsForThisReviewer.map(
              (a) => a.paperId,
            );
            const removing = memberActionKey === researcher.id;
            const localTestUser = isLocalTestUser(researcher.email);
            const isSelf = reviewerUid === user?.uid;
            return (
              <div
                key={researcher.id}
                className="border border-slate-200 rounded-lg p-4"
              >
                <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0 bg-slate-100 p-2 rounded-full">
                      <User className="text-blue-600 h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-lg text-slate-800 truncate">
                        {researcher.displayName || researcher.email}
                      </h3>
                      <div className="text-sm text-slate-500 flex flex-wrap items-center gap-2">
                        <span className="truncate">{researcher.email}</span>
                        {localTestUser && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            local test user
                          </span>
                        )}
                        {isSelf && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                            you
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveResearcher(researcher)}
                    disabled={removing || isSelf}
                    className="inline-flex items-center gap-2 self-start px-3 py-2 bg-white text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {removing ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Remove
                  </button>
                </div>
                <div className="space-y-4 pl-4 border-l-2 border-slate-100 ml-4">
                  {assignedPaperIds.length > 0 ? (
                    assignedPaperIds.map((paperId) => {
                      const paper = papersById[paperId];
                      if (!paper) {
                        return (
                          <div
                            key={paperId}
                            className="p-3 bg-red-50 text-red-700 rounded-lg text-sm"
                          >
                            Error: Paper with ID {paperId} could not be found in
                            the current configuration. Please check assignments
                            in the config manager.
                          </div>
                        );
                      }
                      // Ticket 10: post-backfill, the membership doc id
                      // is `{uid}_{orgId}` and response doc ids are
                      // `${studyId}_${uid}_${paperUuid}`. getResponseDocId
                      // returns the new shape and we pull the uid from
                      // the denormalized field on the membership doc.
                      const responseData = allResponses.get(
                        getResponseDocId(reviewerUid, paper, studyId),
                      );

                      const {
                        answered: answeredCount,
                        total: totalVisibleQuestions,
                        percentage: progress,
                      } = calculatePaperProgress(
                        paper.id,
                        responseData?.answers || responseData?.responses || {}
                      );

                      const isCompleted = progress >= 100;
                      const isSubmitted = responseData?.submitted || false;

                      return (
                        <div
                          key={paperId}
                          className="p-4 bg-slate-50 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                        >
                          <div className="flex-grow">
                            <p className="font-semibold text-slate-800">
                              {paper.title}
                            </p>
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-2">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                {isSubmitted ? (
                                  <Lock size={14} className="text-yellow-600" />
                                ) : isCompleted ? (
                                  <CheckCircle
                                    size={14}
                                    className="text-green-600"
                                  />
                                ) : (
                                  <Edit size={14} className="text-blue-500" />
                                )}
                                <span>
                                  {answeredCount} / {totalVisibleQuestions}{' '}
                                  answered
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm w-48">
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      isCompleted
                                        ? 'bg-green-500'
                                        : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                  ></div>
                                </div>
                                <span className="font-medium text-slate-700 w-10 text-right">
                                  {progress.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleDownloadJson(
                                responseData,
                                researcher.email,
                                paper.title
                              )
                            }
                            disabled={!responseData}
                            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                          >
                            <Download size={16} />
                            <span>JSON</span>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 italic p-4">
                      No papers assigned to this researcher.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
